import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Filter } from 'lucide-react'
import AdminLayout from '../components/AdminLayout'
import AdminStatsRow from '../components/AdminStatsRow'
import { AdminTable, Column } from '../components/AdminTable'
import AdminDateRangePicker, { IsoRange } from '../components/AdminDateRangePicker'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'

type FilingRow = {
  key: string
  type: string
  company: string
  channel: string
  submittedBy: string
  period: string
  filingDate: string
  updatedAt: string
  status: string
  statusTone: 'success' | 'warning' | 'danger'
}

type StatusFilter = 'all' | 'draft' | 'submitted' | 'accepted' | 'rejected'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
]

export default function AdminFilingManagementPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<FilingRow[]>([])
  const [stats, setStats] = useState({ total: 0, successful: 0, failed: 0, pending: 0 })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [filterOpen, setFilterOpen] = useState(false)
  const [range, setRange] = useState<IsoRange>({})
  const filterRef = useRef<HTMLDivElement>(null)
  const itemsPerPage = 10
  const rangeStart = range.start
  const rangeEnd = range.end

  const formatDate = (value?: string) =>
    value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400)
    return () => clearTimeout(t)
  }, [search])

  // New search / filter / range starts back at page 1.
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, statusFilter, rangeStart, rangeEnd])

  // Close the filter menu on outside click.
  useEffect(() => {
    if (!filterOpen) return
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [filterOpen])

  useEffect(() => {
    const loadFilings = async () => {
      setError(null)
      setLoading(true)
      try {
        const { response } = await adminService.listFilings({
          page: currentPage,
          limit: itemsPerPage,
          ...(debouncedSearch ? { q: debouncedSearch } : {}),
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(rangeStart ? { start: rangeStart } : {}),
          ...(rangeEnd ? { end: rangeEnd } : {}),
        })
        const data: any = (response as any)?.data || response
        const items = data?.items || data?.data?.items || data?.data || []
        const summary = data?.summary || data?.data?.summary || {}

        // Pagination uses the filtered count; the cards show global totals.
        setTotalItems(data?.total ?? summary.total ?? 0)
        setStats({
          total: summary.total ?? 0,
          successful: summary.successful ?? 0,
          failed: summary.failed ?? 0,
          pending: summary.pending ?? 0,
        })

        setRows(
          items.map((item: any, idx: number) => {
            const statusRaw = (item.status || item.filingStatus || '').toString()
            const statusValue = statusRaw.toLowerCase().replace(/[_-]/g, ' ')
            const statusTone: FilingRow['statusTone'] = ['submitted', 'completed', 'accepted', 'successful'].includes(statusValue)
              ? 'success'
              : statusValue.includes('draft') || statusValue.includes('pending') || statusValue.includes('progress')
              ? 'warning'
              : 'danger'
            const status = statusValue
              ? statusValue
                  .split(' ')
                  .filter(Boolean)
                  .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
              : '—'

            const period =
              item.period ||
              item.taxYear ||
              (item.accountingPeriodStart && item.accountingPeriodEnd
                ? `${formatDate(item.accountingPeriodStart)} - ${formatDate(item.accountingPeriodEnd)}`
                : '—')

            return {
              key: item.id || item._id || `${idx}`,
              type: item.type || item.filingType || '—',
              company: item.taxpayer || item.companyName || item.company?.name || item.nino || '—',
              channel: item.channel || item.platform || '—',
              submittedBy:
                item.submittedBy?.name ||
                [item.submittedBy?.firstName, item.submittedBy?.lastName].filter(Boolean).join(' ') ||
                item.submittedBy?.email ||
                '—',
              period,
              filingDate: formatDate(item.filingDate),
              updatedAt: formatDate(item.lastUpdated || item.updatedAt),
              status,
              statusTone,
            }
          })
        )
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load filings.')
      } finally {
        setLoading(false)
      }
    }

    void loadFilings()
  }, [currentPage, debouncedSearch, statusFilter, rangeStart, rangeEnd])

  const columns: Column<FilingRow>[] = [
    { key: 'type', label: 'Type' },
    { key: 'company', label: 'Company Name' },
    { key: 'channel', label: 'Channel' },
    { key: 'submittedBy', label: 'Submitted By' },
    { key: 'period', label: 'Filing Period' },
    { key: 'filingDate', label: 'Filing Date' },
    { key: 'updatedAt', label: 'Last Updated' },
    {
      key: 'status',
      label: 'Status',
      render: row => {
        const style =
          row.statusTone === 'success'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
            : row.statusTone === 'warning'
            ? 'bg-amber-50 text-amber-700 border-amber-100'
            : 'bg-red-50 text-red-700 border-red-100'
        return (
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${style}`}>
            {row.status}
          </span>
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <button
          onClick={() => navigate(`/admin/filings/${row.key}`)}
          className='inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50'
        >
          View
        </button>
      ),
    },
  ]

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>Filing Management</h1>
            <p className='text-sm text-gray-500'>Every CT600 and Self Assessment filing on BritFiler.</p>
          </div>
          <div className='self-start sm:self-auto'>
            <AdminDateRangePicker onChange={setRange} />
          </div>
        </div>

        <AdminStatsRow
          stats={[
            { label: 'Total Filings', value: stats.total },
            { label: 'Successful Filings', value: stats.successful },
            { label: 'Failed Filings', value: stats.failed },
            { label: 'Pending Filings', value: stats.pending },
          ]}
        />

        {error && (
          <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
            {error}
          </div>
        )}

        <AdminTable<FilingRow>
          columns={columns}
          data={rows}
          loading={loading}
          emptyMessage='No filings found.'
          searchPlaceholder='Search Filings'
          searchValue={search}
          onSearchChange={setSearch}
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          actions={
            <div className='relative' ref={filterRef}>
              <button
                onClick={() => setFilterOpen(o => !o)}
                className='inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50'
              >
                <Filter className='h-4 w-4 text-gray-500' />
                Filter
                {statusFilter !== 'all' && (
                  <span className='ml-1 rounded-full bg-[#ff4f3c] px-2 py-0.5 text-xs font-semibold text-white capitalize'>
                    {statusFilter}
                  </span>
                )}
              </button>
              {filterOpen && (
                <div className='absolute right-0 z-20 mt-2 w-44 rounded-xl border border-gray-200 bg-white p-1 shadow-lg'>
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setStatusFilter(opt.value)
                        setFilterOpen(false)
                      }}
                      className={`w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                        statusFilter === opt.value ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          }
        />
      </div>
    </AdminLayout>
  )
}
